import { ShoppingCart } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Price } from "@/components/ui/price";
import { Rating } from "@/components/ui/rating";
import { fa } from "@/lib/utils";
import ImageFallback from "@/components/ImageFallback";

export interface Product {
  id: string;
  name: string;
  price: number;
  /** Original price before discount; the percent is computed. */
  original?: number;
  rating?: number;
  reviews?: number;
  badge?: string;
  image?: React.ReactNode;
  outOfStock?: boolean;
}

/** شبکه‌ی محصول. Four-up product cards with a toman price, discount and an add-to-cart row. */
export function ProductGrid({ title, products, onAdd, href }: { title?: string; products: Product[]; onAdd?: (p: Product) => void; href?: (p: Product) => string | undefined }) {
  return (
    <section className="px-4 py-10 sm:px-6 sm:py-16">
      {title && (
        <div className="mx-auto mb-6 flex w-full flex-wrap items-center justify-between gap-2 sm:mb-8">
          <h2 className="text-xl font-bold sm:text-2xl">{title}</h2>
          <a href="#" className="text-sm text-muted-foreground hover:text-foreground">دیدن همه</a>
        </div>
      )}
      <ul className="mx-auto grid w-full grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
        {products.map((p) => {
          const to = href?.(p);
          const inner = (
            <>
              <div className="relative aspect-square w-[7.25rem] shrink-0 self-stretch bg-secondary sm:aspect-square sm:w-full sm:self-auto">
                {p.image ?? <ImageFallback label={p.name} seed={String(p.id)} className="size-full" />}
                {p.badge && <Badge variant="brand" className="absolute end-1.5 top-1.5 rounded-full bg-card px-1.5 text-[10px] sm:end-3 sm:top-3 sm:px-2 sm:text-xs">{p.badge}</Badge>}
                {p.outOfStock && <div className="absolute inset-0 flex items-center justify-center bg-background/70 text-[11px] font-medium sm:text-sm">ناموجود</div>}
              </div>
              <div className="flex min-w-0 flex-1 flex-col justify-between gap-2 p-3 sm:p-4">
                <div className="min-w-0">
                  <h3 className="line-clamp-2 min-h-12 text-sm font-medium leading-6">{p.name}</h3>
                  {p.rating !== undefined && (
                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                      <Rating value={p.rating} readOnly size="sm" />
                      {p.reviews !== undefined && <span>({fa(p.reviews)})</span>}
                    </div>
                  )}
                </div>
                <div className="flex min-h-14 items-end justify-between gap-2">
                  <Price amount={p.price} original={p.original} size="sm" className="min-w-0" />
                  <Button size="icon" variant="outline" className="size-11 shrink-0 sm:size-10" aria-label={`افزودن ${p.name} به سبد`} disabled={p.outOfStock} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onAdd?.(p); }}><ShoppingCart /></Button>
                </div>
              </div>
            </>
          );
          return (
            <li key={p.id} className="group flex h-full min-w-0 flex-row overflow-hidden rounded-[var(--shape-card,1rem)] border border-border bg-card sm:flex-col">
              {to ? <a href={to} className="flex min-w-0 flex-1 flex-row sm:flex-col" style={{ color: 'inherit', textDecoration: 'none' }}>{inner}</a> : inner}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
